"use client";

import { useMemo, useState } from "react";
import { HiClipboardCopy, HiCheck, HiVideoCamera, HiRefresh } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const TIKTOK_CAPTION_LIMIT = 2200;

type Niche =
  | "fitness"
  | "beauty"
  | "food"
  | "comedy"
  | "tutorial"
  | "vlog"
  | "business"
  | "fashion"
  | "education"
  | "tech";

type Goal = "views" | "follows" | "engagement" | "link";

const NICHE_LABELS: Record<Niche, string> = {
  fitness: "Fitness",
  beauty: "Beauty",
  food: "Food",
  comedy: "Comedy",
  tutorial: "Tutorial",
  vlog: "Vlog",
  business: "Business",
  fashion: "Fashion",
  education: "Education",
  tech: "Tech",
};

const GOAL_LABELS: Record<Goal, string> = {
  views: "More views",
  follows: "More follows",
  engagement: "More comments / shares",
  link: "Drive to link in bio",
};

const HOOKS = [
  "POV: you just discovered {topic}",
  "Nobody is talking about {topic} but they should be",
  "I tried {topic} for 30 days. Here's what happened",
  "Stop scrolling if you care about {topic}",
  "Things I wish I knew about {topic} sooner",
  "The {topic} hack nobody told you about",
  "Watch until the end if you want to {topic}",
  "Tell me you don't know {topic} without telling me",
  "This is your sign to start {topic}",
  "Wait, why is nobody talking about {topic}?",
  "If you do {topic}, this changes everything",
  "{topic} but make it actually work",
];

const CTAS_BY_GOAL: Record<Goal, string[]> = {
  views: [
    "Watch till the end for the result 👀",
    "Save this for the next time you forget 💾",
    "Send this to someone who needs to see it",
    "Re-watch — there are 3 details you missed",
  ],
  follows: [
    "Follow for more {niche} content like this",
    "More {niche} on the page — follow if you're into it",
    "Following for part 2? Drop a 🙋",
    "Hit the follow if this changed your mind",
  ],
  engagement: [
    "Drop your hot take in the comments 👇",
    "Tag someone who needs to read this",
    "Comment your version below — I'll feature the best ones",
    "Save this and come back with your result",
  ],
  link: [
    "Link in bio for the full guide 🔗",
    "Full breakdown in bio — go grab it",
    "I made a free resource — link in bio",
    "More details + the template in bio",
  ],
};

const HASHTAGS: Record<Niche, string[]> = {
  fitness: [
    "#fittok",
    "#workout",
    "#gym",
    "#fitness",
    "#fitnessmotivation",
    "#homeworkout",
    "#fitnessjourney",
  ],
  beauty: [
    "#beautytok",
    "#makeup",
    "#skincare",
    "#makeuptutorial",
    "#grwm",
    "#beautyhacks",
    "#beauty",
  ],
  food: ["#foodtok", "#easyrecipe", "#cooking", "#recipe", "#foodie", "#quickmeals", "#mealprep"],
  comedy: ["#comedy", "#funny", "#relatable", "#fyp", "#humor", "#meme", "#viral"],
  tutorial: ["#howto", "#tutorial", "#learnontiktok", "#diy", "#tips", "#hacks", "#stepbystep"],
  vlog: [
    "#dayinmylife",
    "#vlog",
    "#dailyroutine",
    "#lifestyle",
    "#aesthetic",
    "#routine",
    "#productivity",
  ],
  business: [
    "#entrepreneur",
    "#smallbusiness",
    "#businesstips",
    "#sidehustle",
    "#startup",
    "#marketing",
    "#sales",
  ],
  fashion: [
    "#fashiontok",
    "#ootd",
    "#styletips",
    "#fashion",
    "#outfitideas",
    "#stylehacks",
    "#fashioninspo",
  ],
  education: [
    "#learnontiktok",
    "#education",
    "#studytok",
    "#facts",
    "#didyouknow",
    "#explained",
    "#study",
  ],
  tech: ["#techtok", "#gadgets", "#techreview", "#smartphone", "#tech", "#productivity", "#apps"],
};

function pick<T>(arr: readonly T[], seed: number): T {
  return arr[seed % arr.length];
}

function fillTemplate(template: string, topic: string, niche: Niche): string {
  return template
    .replaceAll("{topic}", topic)
    .replaceAll("{niche}", NICHE_LABELS[niche].toLowerCase());
}

interface Caption {
  hook: string;
  body: string;
  cta: string;
  hashtags: string[];
  full: string;
}

function generateCaption(topic: string, niche: Niche, goal: Goal, seed: number): Caption {
  const hookTpl = pick(HOOKS, seed);
  const ctaTpl = pick(CTAS_BY_GOAL[goal], seed >> 2);
  const hashtagPool = HASHTAGS[niche];
  const hashtags: string[] = [];
  for (let i = 0; i < 5 && i < hashtagPool.length; i += 1) {
    hashtags.push(hashtagPool[(seed + i * 3) % hashtagPool.length]);
  }
  const dedupedHashtags = Array.from(new Set(hashtags));

  const hook = fillTemplate(hookTpl, topic, niche);
  const cta = fillTemplate(ctaTpl, topic, niche);
  const body = "Here's the version that actually worked for me — no fluff, just the steps.";

  const full = `${hook}\n\n${body}\n\n${cta}\n\n${dedupedHashtags.join(" ")}`;
  return { hook, body, cta, hashtags: dedupedHashtags, full };
}

const FAQ_ITEMS = [
  {
    question: "What's TikTok's 2,200-character caption limit?",
    answer:
      "TikTok now allows captions up to 2,200 characters (raised from 150 in 2022). The tool stays well under that — long enough to add context and CTAs, short enough to keep the algorithm-favored 'first line hook' visible without truncation.",
  },
  {
    question: "Are these captions any good?",
    answer:
      "They follow the structure that performs reliably on TikTok: a scroll-stopping hook in line 1, a payoff/promise in the body, a CTA matched to your stated goal, and niche-relevant hashtags. They're a strong starting point — edit them to fit your voice and the specifics of your video.",
  },
  {
    question: "How many hashtags should I use?",
    answer:
      "3 to 5 niche-relevant hashtags consistently outperforms loading 20+. The tool gives you 5 by default. TikTok prioritizes relevance over volume; spammy hashtag stuffing can flag the post for reduced reach.",
  },
  {
    question: "Why does the first line matter so much?",
    answer:
      "TikTok shows only the first ~1-2 lines of your caption above the video controls before the user has to tap 'more'. The hook in line 1 is what users see while deciding whether to watch — so it has to do the work of grabbing attention on its own.",
  },
  {
    question: "Are my captions sent anywhere?",
    answer:
      "No. Everything is generated in your browser from local templates. No API calls, no logging, nothing leaves your machine. Verify in the browser's network tab.",
  },
];

export default function TiktokCaptionGeneratorClient() {
  const [topic, setTopic] = useState("");
  const [niche, setNiche] = useState<Niche>("fitness");
  const [goal, setGoal] = useState<Goal>("views");
  const [seed, setSeed] = useState(0);
  const [copied, setCopied] = useState<string | null>(null);

  const caption = useMemo(() => {
    if (!topic.trim()) return null;
    return generateCaption(topic.trim(), niche, goal, seed);
  }, [topic, niche, goal, seed]);

  const copy = async (content: string, key: string) => {
    await navigator.clipboard.writeText(content);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="TikTok Caption Generator"
        description="Generate TikTok captions with proven hooks, CTAs, and niche hashtags. Stays under TikTok's 2,200-character limit."
        url="https://plugmonkey.xyz/tool/tiktok-caption-generator/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "TikTok Caption Generator",
            url: "https://plugmonkey.xyz/tool/tiktok-caption-generator/",
          },
        ]}
        faq={FAQ_ITEMS}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-pink-500/10 rounded-full text-pink-500">
          <HiVideoCamera className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">TikTok Caption Generator</h1>
          <p className="text-muted-foreground mt-1">
            Hook + body + CTA + hashtags. Tuned by niche and goal.
          </p>
        </div>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Video Topic</CardTitle>
          <CardDescription>What is the video about? Be specific.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <input
            id="video-topic"
            name="video-topic"
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. cold plunge mornings, 5-min air fryer dinners, no-makeup makeup"
            className="w-full px-4 py-3 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label="Video topic"
          />

          <div>
            <div className="text-sm font-semibold mb-2">Niche</div>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(NICHE_LABELS) as Niche[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setNiche(key)}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    niche === key
                      ? "border-primary bg-primary text-white"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {NICHE_LABELS[key]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold mb-2">Goal</div>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(GOAL_LABELS) as Goal[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setGoal(key)}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    goal === key
                      ? "border-primary bg-primary text-white"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {GOAL_LABELS[key]}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {caption && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
              <div>
                <CardTitle>
                  Caption · {caption.full.length}/{TIKTOK_CAPTION_LIMIT} chars
                </CardTitle>
                <CardDescription className="mt-1">
                  First line is the hook — keep it tight.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSeed((s) => s + 1)}>
                  <HiRefresh className="w-4 h-4" />
                  Regenerate
                </Button>
                <Button size="sm" onClick={() => copy(caption.full, "all")}>
                  {copied === "all" ? (
                    <HiCheck className="w-4 h-4" />
                  ) : (
                    <HiClipboardCopy className="w-4 h-4" />
                  )}
                  {copied === "all" ? "Copied" : "Copy"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <pre className="font-sans whitespace-pre-wrap text-sm border border-border rounded-md bg-muted/30 p-4">
                {caption.full}
              </pre>
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="text-xs uppercase tracking-wide font-mono text-muted-foreground">
                  Hook
                </div>
                <div className="font-semibold">{caption.hook}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="text-xs uppercase tracking-wide font-mono text-muted-foreground">
                  CTA
                </div>
                <div className="font-semibold">{caption.cta}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="text-xs uppercase tracking-wide font-mono text-muted-foreground">
                  Hashtags
                </div>
                <div className="font-semibold font-mono text-xs">{caption.hashtags.join(" ")}</div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <div className="mt-16">
        <ToolsCustomDevCTA />
      </div>

      <div className="mt-16">
        <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
        <UnifiedFAQ items={FAQ_ITEMS} showSupportLink={false} className="space-y-4" />
      </div>
    </div>
  );
}
